import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;
  #followingAppAdd = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      // A product options / upload app already took over this add (it called preventDefault).
      // Don't add a second time; wait for the app's add to land, then refresh and open the drawer.
      if (event.defaultPrevented && event.target instanceof HTMLFormElement) {
        // Mark the hand-off so that if the app redirects to /cart (e.g. after editing a cart item),
        // the cart page sends the shopper back here with the drawer open (see layout/theme.liquid).
        try {
          sessionStorage.setItem('cp:app-add', JSON.stringify({ path: location.pathname, t: Date.now() }));
        } catch {
          // Storage unavailable (private mode quirks): the app's own redirect still works.
        }
        this.#followAppAdd(event.submitter || this.querySelector('button[name="add"], button[type="submit"]'));
        return;
      }
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    // The theme is handling this add itself and opens the drawer, so no app redirect to /cart is coming.
    try { sessionStorage.removeItem('cp:app-add'); } catch {}
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');
    // Snapshot the cart first so a reported failure can be double-checked: if an app added
    // the item at the same time, the cart still grew and the shopper should see success.
    const countBefore = this.#cartCount();

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage && !(await this.#cartGrewSince(countBefore))) {
        this.#showError(errorMessage);
      } else {
        await this.#refreshAndOpenCart();
      }
    } catch (error) {
      if (signal.aborted) return;

      if (await this.#cartGrewSince(countBefore)) {
        await this.#refreshAndOpenCart();
      } else {
        this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
      }
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  /** Resolves to the cart's item count, or null if it can't be read. */
  async #cartCount() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return null;
      const cart = await response.json();
      return typeof cart.item_count === 'number' ? cart.item_count : null;
    } catch {
      return null;
    }
  }

  async #cartGrewSince(countBeforePromise) {
    const before = await countBeforePromise;
    if (before == null) return false;
    const after = await this.#cartCount();
    return after != null && after > before;
  }

  async #refreshAndOpenCart() {
    try { await window.cpRemoveEditedOriginal?.(); } catch {}
    try {
      await customElements.get('s-cart')?.refreshMountedCarts?.();
    } catch {
      // A stale drawer is better than no drawer; opening still shows the cart.
    }
    await window.Shopify?.actions?.openCart?.();
  }

  /** An app is adding the item itself: poll briefly for the cart to grow, then show it. */
  async #followAppAdd(button = null) {
    if (this.#followingAppAdd) return;
    this.#followingAppAdd = true;
    // Show the button as busy while the app saves and the cart catches up (can take a few seconds).
    this.#setSubmitting(true, button);
    try {
      const before = await this.#cartCount();
      if (before == null) return;
      for (let attempt = 0; attempt < 16; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (!this.isConnected) return;
        const now = await this.#cartCount();
        if (now != null && now > before) {
          // Keep the cp:app-add mark: the app may still send the shopper to /cart, and the cart page bounces back.
          await this.#refreshAndOpenCart();
          return;
        }
      }
    } finally {
      this.#followingAppAdd = false;
      this.#setSubmitting(false);
    }
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait', 'cp-loading');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      this.#activeSubmitter.classList.remove('cp-loading');
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}


/**
 * Edit-item flow: when a shopper taps Edit in the cart, the product options app opens the product page and, on
 * Add to cart, saves the item itself and sends the shopper to /cart, without the form submit the theme listens
 * for. So mark the hand-off on the tap itself (capture phase, before the app can stop it); the cart page then
 * bounces back here with the drawer open (see the cart check in layout/theme.liquid's head).
 * Tapping a real cart link clears the mark, so "View cart" still goes to the cart page.
 */
const ADD_TEXT = /add to cart|update|save/i;
window.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const cartLink = target.closest('a[href]');
  if (cartLink && /\/cart\/?$/.test(new URL(cartLink.href, location.href).pathname)) {
    try { sessionStorage.removeItem('cp:app-add'); } catch {}
    return;
  }

  if (!location.pathname.includes('/products/')) return;
  const control = target.closest('button, [role="button"], input[type="submit"]');
  if (!control || !control.closest('s-product-form, .pdp-form')) return;
  const looksLikeAdd = control.getAttribute('name') === 'add' || control.getAttribute('type') === 'submit' || ADD_TEXT.test(control.textContent || control.value || '');
  if (!looksLikeAdd) return;
  try {
    sessionStorage.setItem('cp:app-add', JSON.stringify({ path: location.pathname, t: Date.now() }));
  } catch {
    // Storage unavailable: the app's own redirect still works.
  }
}, { capture: true });


/**
 * "Stitching in your changes" cover for cart edits on every device. On phones the options app sends the shopper to
 * /cart after an edit (and the cart page shows the cover); on PC it can save in place, so nothing showed. Tapping
 * Update while editing arms the cover; it appears only once the app actually sends its cart request (so a missing
 * required field never leaves it up), and lifts when the cart drawer is open, the page leaves, or after 8s.
 */
let cpCoverArmedAt = 0;
const CART_WRITE = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
function cpIsEditing() {
  try { return !!sessionStorage.getItem('cp:editing'); } catch { return false; }
}
function cpShowEditCover() {
  if (!cpCoverArmedAt || Date.now() - cpCoverArmedAt > 15000) return;
  cpCoverArmedAt = 0;
  const html = document.documentElement;
  if (html.classList.contains('cp-bouncing')) return;
  html.classList.add('cp-bouncing');
  const started = Date.now();
  const lift = () => requestAnimationFrame(() => requestAnimationFrame(() => html.classList.remove('cp-bouncing')));
  (function watch() {
    const drawer = document.getElementById('cart-drawer');
    const open = !!(drawer && (drawer.open || drawer.hasAttribute('open')));
    if (open) { setTimeout(lift, 150); return; }
    if (Date.now() - started > 8000) { html.classList.remove('cp-bouncing'); return; }
    setTimeout(watch, 120);
  })();
}
function cpWatchCartWrite(url) {
  try {
    if (cpCoverArmedAt && CART_WRITE.test(new URL(String(url), location.href).pathname)) cpShowEditCover();
  } catch {}
}
if (location.pathname.includes('/products/') && !window.cpEditCoverPatched) {
  window.cpEditCoverPatched = true;
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    cpWatchCartWrite(input instanceof Request ? input.url : input);
    return nativeFetch.apply(this, arguments);
  };
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    cpWatchCartWrite(url);
    return nativeOpen.apply(this, arguments);
  };
  window.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const control = target?.closest('button, [role="button"], input[type="submit"]');
    if (!control || !control.closest('s-product-form, .pdp-form') || !cpIsEditing()) return;
    const looksLikeAdd = control.getAttribute('name') === 'add' || control.getAttribute('type') === 'submit' || ADD_TEXT.test(control.textContent || control.value || '');
    if (looksLikeAdd) cpCoverArmedAt = Date.now();
  }, { capture: true });
  // Back-forward cache: never come back to a covered page.
  window.addEventListener('pageshow', (event) => { if (event.persisted) document.documentElement.classList.remove('cp-bouncing'); });
}
